import { ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy, ViewChild } from "@angular/core";
import { Observable, Subscription } from "rxjs";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { IMetadataImage } from "../../viewer-components/interfaces/i-metadataimage";
import { ImageGalleryService } from "../../viewer-components/services/internal-services/image-gallery/image-gallery.service";
import { IGalleryStatus } from "../../viewer-components/interfaces/i-gallery-status";
import { IGalleryCollection } from "../../viewer-components/interfaces/i-gallery-collection";
import { IGalleryFilter } from "../../viewer-components/interfaces/i-gallery-filter";
import { ViewerConstants } from "../../shared/model/viewer-constants";
import { LayerService } from "../../viewer-components/services/internal-services/layer.service";
import { GeoService } from "../../viewer-components/services/internal-services/geo.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../shared/model/google-analytics-constants";
import { MapService } from "../../viewer-components/services/internal-services/map.service";
import { FeatureFlagsService } from "../../viewer-components/services/internal-services/feature-flags.service";
import { FeatureFlags } from "../../viewer-components/model/feature-flags";
import { GoogleAnalyticsService } from "../../shared/services/google-analytics.service";
import { BaseViewerService } from "../../viewer-components/services/internal-services/viewer-services/base-viewer.service";

@Directive()
export abstract class ImageGalleryComponent implements OnDestroy {
  subscriptions: Subscription[] = [];

  _selectedFilter: IGalleryFilter = {
    layername: null,
    resolution: null,
    orientation: null,
  };

  selectedImage: IMetadataImage;

  @Input()
  set collections(collections: IGalleryCollection[]) {
    this._collections = collections;
    this.collectionsReverse = [...collections].reverse();
  }

  get collections(): IGalleryCollection[] {
    return this._collections;
  }

  _collections: IGalleryCollection[] = [];

  collectionsReverse: IGalleryCollection[] = [];

  visibleOrientations = [];

  visibleResolutions: string | any[] = [];

  visibleLayers = [];

  @ViewChild("galleryContainer")
  galleryContainer: ElementRef;

  @Input()
  enableDebugOptions: boolean = false;

  @Input()
  images: IMetadataImage[] = [];

  @Input()
  filteredImages: IMetadataImage[] = [];

  @Input()
  status: IGalleryStatus;

  @Input()
  selectedCollection: IGalleryCollection;

  @Input()
  set discreteEnabled(enabled: boolean) {
    if (!enabled) {
      this.selectImage = null;
    }
  }

  protected constructor(
    public viewerService: BaseViewerService,
    protected googleAnalyticsService: GoogleAnalyticsService,
    public galleryService: ImageGalleryService,
    protected layerService: LayerService,
    protected mapService: MapService,
    protected cdr: ChangeDetectorRef,
    protected flagService: FeatureFlagsService,
  ) {}

  FeatureFlags = FeatureFlags;

  // region LifeCycle

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
  // endregion

  // region Common functions
  /**
   * Get images from data/cache or service
   * with preview url for each image
   * without vertical and infrared orientation
   * Sort by captured date
   * Get origin point
   * Get extent for images
   */
  protected abstract getInitData(): Observable<IGalleryStatus>;

  /**
   * Handle status and show images
   * Update any related with UX
   */
  protected abstract openGallery(status: IGalleryStatus): void;

  /**
   * Delete all data for the gallery
   */
  protected abstract closeGallery();

  /**
   * Launch process necessary at start to show images
   */
  protected abstract initialize(): void;

  protected abstract processGalleryStatus(status: IGalleryStatus): Observable<IGalleryCollection>;

  showFullscreen(image: IMetadataImage, serialNumber: number, event: any): void {
    if (event.target.innerHTML !== ViewerConstants.CONTENT_COPY) {
      this.galleryService.showFullscreen(image, serialNumber);
    }
  }

  closeFullscreen(): void {
    this.galleryService.closeFullscreen();
  }

  /**
   * Debug function to paint on the map the extent and center for the gallery
   */
  paintStatus() {
    if (this.status) {
      const extentWKT = GeoService.polygonToWkt(this.status.extent);
      const wktFeature = this.layerService.createFeatureWkt(
        this.viewerService.getCoreMap(),
        extentWKT,
        ViewerConstants.PROJECTIONS.WebMercator,
        {},
      );
      this.mapService.getDrawingLayer().getSource().addFeature(wktFeature);
    }
  }
  // endregion

  // region Handle Filter updates from service
  /**
   * Update selected filter and visibility for buttons
   * @param filter
   */
  @Input()
  public set selectedFilter(filter: IGalleryFilter) {
    if (!filter) {
      return;
    }
    this._selectedFilter = filter;
    this.updateCategoryVisible(filter);
    this.updateResolutionsVisible(filter);
    this.updateOrientationsVisible(filter);
  }

  get selectedFilter(): IGalleryFilter {
    return this._selectedFilter;
  }

  /**
   * Update list with visible categories
   * @param filter
   */
  updateCategoryVisible(filter: IGalleryFilter) {
    this.visibleLayers = filter && filter.layername ? [filter.layername] : this.selectedCollection?.layers;
  }

  updateResolutionsVisible(filter: IGalleryFilter) {
    const layerType = this.selectedCollection?.types.find((type) => type.name === filter.layername);
    if (!layerType) {
      this.visibleResolutions = [];
    } else if (filter.resolution && layerType.resolutions.includes(filter.resolution)) {
      this.visibleResolutions = [filter.resolution];
    } else {
      this.visibleResolutions = layerType.resolutions;
    }
  }

  updateOrientationsVisible(filter: IGalleryFilter) {
    const layerType = this.selectedCollection?.types.find((type) => type.name === filter.layername);
    if (layerType && layerType.orientations[filter.resolution]) {
      this.visibleOrientations = layerType.orientations[filter.resolution];
    } else {
      this.visibleOrientations = [];
    }
  }
  // endregion

  // region Change filters

  changeCategory(event: MatButtonToggleChange) {
    if (this.categoryLocked()) {
      return;
    }
    const toggle = event.source;
    if (toggle && event.value.some((item) => item === toggle.value)) {
      toggle.buttonToggleGroup.value = [toggle.value];
      this.galleryService.changeLayerFilter(toggle.value);
    } else {
      this.galleryService.changeLayerFilter(null);
    }

    this.cdr.detectChanges();

    this.launchGoogleAnalytics();
  }

  changeResolution(event: MatButtonToggleChange) {
    if (this.resolutionLocked()) {
      return;
    }

    const toggle = event.source;
    if (toggle && event.value.some((item) => item === toggle.value)) {
      toggle.buttonToggleGroup.value = [toggle.value];
      this.galleryService.changeResolutionFilter(toggle.value);
    } else {
      this.galleryService.changeResolutionFilter(null);
    }

    this.cdr.detectChanges();

    this.launchGoogleAnalytics();
  }

  changeOrientation(event: MatButtonToggleChange) {
    const toggle = event.source;
    if (toggle && event.value.some((item) => item === toggle.value)) {
      toggle.buttonToggleGroup.value = [toggle.value];
      this.galleryService.changeOrientationFilter(toggle.value);
    } else {
      this.galleryService.changeOrientationFilter(null);
    }

    this.cdr.detectChanges();

    this.launchGoogleAnalytics();
  }

  launchGoogleAnalytics() {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.IMAGE_GALLERY_DATA_FILTER,
      GoogleAnalyticsConstantsCategory.LEFT_TOOLBAR,
      true,
    );
  }

  // endregion

  // region Handle filter button UX

  categoryLocked(): boolean {
    return this.selectedCollection && this.selectedCollection.types.length === 1;
  }

  resolutionLocked(): boolean {
    const selectedLayer = this.galleryService.getCollectionCategory(
      this.selectedCollection,
      this.selectedFilter.layername,
    );
    return selectedLayer && selectedLayer.resolutions.length === 1;
  }

  /**
   * Check if we should show this resolution button
   * @param resolution
   */
  isResolutionVisible(resolution: string): boolean {
    return this.visibleResolutions.includes(resolution);
  }

  /**
   * Check if the orientation buttons should be visibles
   */
  isOrientationsVisible(): boolean {
    return !!this.selectedFilter.layername && !!this.selectedFilter.resolution;
  }

  /**
   * Check if the orientation buttons should be visibles
   */
  isOrientationVisible(orientation: string): boolean {
    return this.visibleOrientations.includes(orientation);
  }

  /**
   * Check if we need to show this category button
   * If collection contains that layer and if we have some layer selected only show this
   * @param category category name like bluesky, graysky
   */
  isCategoryVisible(category: string): boolean {
    return this.visibleLayers.includes(category);
  }

  // endregion

  // region Selected image

  /**
   * Iterate through container to find <vision-gallery-image> with image-id
   * @param image
   */
  protected findImageElement(image: IMetadataImage): HTMLElement {
    return <HTMLElement>(
      Array.from(this.galleryContainer.nativeElement.querySelectorAll("vision-gallery-image")).find(
        (element: HTMLElement) => element.getAttribute("image-id") === image.image_id,
      )
    );
  }

  /**
   * If we have a selected image change the gallery position to focus on that image
   */
  @Input()
  public set selectImage(image: IMetadataImage) {
    this.selectedImage = image;

    if (!this.galleryContainer) {
      return;
    }
    if (image) {
      const imageElement = this.findImageElement(image);
      if (imageElement && imageElement.getBoundingClientRect().height === 0) {
        setTimeout(() => {
          this.selectImage = this.selectedImage;
        }, 500);
      } else if (imageElement) {
        this.galleryContainer.nativeElement.scrollTop =
          imageElement.offsetTop - this.galleryContainer.nativeElement.offsetTop;
      }
    } else {
      this.galleryContainer.nativeElement.scrollTop = 0;
    }
  }
  // endregion
}
